import React from 'react';
import {Icon, Input, PriorityIcon, Flex} from '@priority-software/priority-style-react';
import {handleSearch, monitorAction} from '../actions';
import styles from '../styles/searchBlock.module.scss';
import texts from '../../texts';
import {debounce} from 'lodash';
import {SettingsPanel} from '../SettingsPanel/SettingsPanel';
import {Column, formsStore, Record} from '../../../../../../stores/Form';
import {useCallback} from 'react';
import {priority} from '@priority-software/priority-proto-api/lib';
import {SettingsPanelCol, MonitoredActions} from '../../Common/consts';
import {createDataTestId} from '../../../../../../utils/dataTestId';

interface searchProps {
    searchQuery: string;
    onSearchChange: (searchInput: string) => void;
    isServerSearch: boolean;
    records: Record[];
    defaultCols: string[];
    enabledCols: string[];
    applySettings: (cols: SettingsPanelCol[]) => Promise<void>;
    componentClasses?: string;
    subformType: priority.netitems.form.SubformType;
}

export const SearchBlock = ({
    searchQuery,
    onSearchChange,
    isServerSearch,
    records,
    defaultCols,
    enabledCols,
    componentClasses,
    applySettings,
    subformType
}: searchProps) => {
    const columns: Column[] = [];
    const searchableColumnsNames = [...defaultCols, ...enabledCols];
    const headerHasTabs = subformType === 3 || subformType === 4;

    if (isServerSearch) {
        searchableColumnsNames.forEach(val => {
            const column = formsStore.currentScreen.getActiveForm().getColumnByName(val);
            if (
                column &&
                column?.Type !== priority.netitems.form.ColumnType.Boolean &&
                column?.Type !== priority.netitems.form.ColumnType.Char
            ) {
                columns.push(column);
            }
        });
    }

    const debouncedRequest = useCallback(
        debounce(async params => {
            const {searchInput, columns, session} = params;
            await handleSearch(session, searchInput, columns);
        }, 450),
        []
    );

    const handleSearchChange = async e => {
        if (isServerSearch) {
            debouncedRequest({
                searchInput: e.target.value,
                columns,
                session: formsStore.currentSession
            });
        }
        onSearchChange(e.target.value);
    };

    const handleClearSearch = async () => {
        if (isServerSearch) {
            await handleSearch(formsStore.currentSession, '', columns);
        }
        onSearchChange('');
    };

    const monitorSearchBlock = (action: MonitoredActions) => {
        const subformName = formsStore?.currentScreen?.getActiveForm()?.ename;
        monitorAction({subformType, action, subformName});
    };

    return (
        <div className={componentClasses}>
            <Flex justifyContent={'space-between'} componentClasses={styles.searchBlock}>
                <Input
                    {...createDataTestId(`search-input`, true)}
                    placeholder={texts.search_placeholder}
                    onChange={handleSearchChange}
                    value={searchQuery}
                    size="large"
                    roundedCorners
                    onFocus={() => monitorSearchBlock(MonitoredActions.Search)}
                    children={
                        <>
                            {searchQuery ? (
                                <Icon
                                    icon={PriorityIcon.closeSmall}
                                    iconColor="primary-700"
                                    componentClasses={styles.CloseSmallbtn}
                                    onClick={handleClearSearch}
                                />
                            ) : (
                                <Icon
                                    icon={PriorityIcon.searchThin}
                                    iconColor="primary-700"
                                    componentClasses={styles.searchBtn}
                                />
                            )}
                        </>
                    }
                />
                <SettingsPanel
                    records={records}
                    defaultCols={defaultCols}
                    enabledCols={enabledCols}
                    applyChanges={applySettings}
                    monitorSearchBlock={monitorSearchBlock}
                    headerHasTabs={headerHasTabs}
                />
            </Flex>
        </div>
    );
};
